import { Collection, Database } from '@nozbe/watermelondb';

import { ProductModel, ProductPayload } from '../../../../types/Products';
import { DBWriterLoggerFunction } from '../../../../types/logger';
import {
    InventoryProductModel,
    InventoryProductPayload,
} from '../../../../types/InventoryProduct';
import {
    InventoryChangeModel,
    InventoryChangePayload,
} from '../../../../types/InventoryChange';

export const prepareCreateProduct = ({
    payload,
    userId,
    organisationID,
    collection,
}: {
    payload: ProductPayload;
    userId: string;
    organisationID: string;
    collection: Collection<ProductModel>;
}) =>
    collection.prepareCreate((product) => {
        product.name = payload.name;
        product.productType = payload.productType;
        product.brand = payload.brand;
        product.family = payload.family;
        product.shape = payload.shape;
        product.dimensions = payload.dimensions;
        product.clips = payload.clips;
        product.shoeSize = payload.shoeSize;
        product.nailSize = payload.nailSize;
        product.skuApac = payload.skuApac;
        product.skuEmea = payload.skuEmea;
        product.uomDealers = payload.uomDealers;
        product.material = payload.material;
        product.boxUnitSize = payload.boxUnitSize ?? 0;
        product.userId = userId;
        product.organisationId = organisationID;
        product.notAvailable = payload.notAvailable;
    });

export const prepareProductInventoryRecords = ({
    inventoryProductPayload,
    inventoryChangePayload,
    product,
    userId,
    organisationID,
    database,
}: {
    inventoryProductPayload?: Omit<InventoryProductPayload, 'productId'>;
    inventoryChangePayload?: Omit<InventoryChangePayload, 'inventoryProductId'>;
    product: ProductModel;
    userId: string;
    organisationID: string;
    database: Database;
}): {
    inventoryProduct?: InventoryProductModel;
    inventoryChange?: InventoryChangeModel;
} => {
    if (!inventoryProductPayload) return {};

    const inventoryProduct = database.collections
        .get<InventoryProductModel>('inventory_products')
        .prepareCreate((record) => {
            record.productId = product.id;
            record.quantity = inventoryProductPayload.quantity;
            record.favourite = inventoryProductPayload.favourite;
            record.userId = userId;
            record.organisationId = organisationID;
        });

    const inventoryChange = inventoryChangePayload
        ? database.collections
              .get<InventoryChangeModel>('inventory_changes')
              .prepareCreate((record) => {
                  record.inventoryProductId = inventoryProduct.id;
                  record.quantityChange = inventoryChangePayload.quantityChange;
                  record.userId = userId;
                  record.organisationId = organisationID;
                  record.processAt = inventoryChangePayload.processAt;
              })
        : undefined;

    return { inventoryProduct, inventoryChange };
};

export const logAddProductActions = ({
    product,
    inventoryProduct,
    inventoryChange,
    logDBAction,
    tableName,
}: {
    product: ProductModel;
    inventoryProduct?: InventoryProductModel;
    inventoryChange?: InventoryChangeModel;
    logDBAction: DBWriterLoggerFunction;
    tableName: string;
}) => {
    logDBAction({
        message: 'Create product',
        modelName: tableName,
        payload: product,
    });

    if (inventoryProduct) {
        logDBAction({
            message: 'Create inventory product',
            modelName: 'inventory_products',
            payload: inventoryProduct,
        });
    }
    if (inventoryChange) {
        logDBAction({
            message: 'Create inventory change',
            modelName: 'inventory_changes',
            payload: inventoryChange,
        });
    }
};
